import React, { useCallback, useEffect, useState } from 'react';
import { operations } from '../services/api';
import QuickActions from '../components/Operations/QuickActions';
import ManualEntryDialog from '../components/Operations/ManualEntryDialog';
import WhatsAppDialog from '../components/Operations/WhatsAppDialog';
import SmsDialog from '../components/Operations/SmsDialog';
import BookClassDialog from '../components/Operations/BookClassDialog';

// Receção operacional — AGORA / HOJE. Tudo capability-aware: o backend só devolve blocos dos
// módulos ativos. Não inventamos "quem está dentro" (AccessEvent é só entrada; sem eventos de saída).
// As ações rápidas do balcão vivem em components/Operations/QuickActions: cada uma abre o ecrã que a faz.

const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '—');

export default function ReceptionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [dialog, setDialog] = useState(null);   // 'manual-entry' | 'whatsapp' | 'sms' | 'book-class' | null

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await operations.reception();
      setData(res.data);
      setError(false);
      setRefreshedAt(new Date());
    } catch (e) {
      // 403 = módulo Receção desligado neste ginásio
      setError(e?.response?.status === 403 ? 'disabled' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), 30000); // auto-refresh AGORA a cada 30s
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <div style={S.wrap}><p>A carregar receção…</p></div>;
  if (error === 'disabled') return <div style={S.wrap}><div style={S.empty}>O módulo de Receção está desligado neste ginásio.</div></div>;
  if (error || !data) return <div style={S.wrap}><div style={S.empty}>Não foi possível carregar a receção.</div></div>;

  const now = data.now || {};
  const today = data.today || {};
  const caps = data.capabilities || {};
  const entries = now.entries || [];
  const denied = now.denied || [];
  const classes = now.classesStarting || [];
  const alerts = now.paymentAlerts;

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.h2}>Receção</h2>
        <div style={S.refresh}>
          {/* «Registar entrada» já vem nas ações rápidas quando o controlo de acessos está ligado; aqui só se não vier (antes aparecia duas vezes). */}
          {!(Array.isArray(data.quickActions) && data.quickActions.includes('manual-entry')) && (
            <button type="button" style={S.refreshBtn} onClick={() => setDialog('manual-entry')}>Registar entrada</button>
          )}
          Atualizado {fmtTime(refreshedAt)}
          <button style={S.refreshBtn} onClick={() => load(false)}>Atualizar</button>
        </div>
      </div>

      {/* Ações rápidas — só as suportadas pelos módulos ativos; cada uma abre o ecrã (ou o diálogo) que a faz */}
      <QuickActions
        actions={data.quickActions}
        onDialog={setDialog}
        style={S.actions}
        buttonStyle={S.action}
        disabledStyle={S.actionDisabled}
      />

      {/* HOJE — contadores (só métricas de módulos ligados) */}
      <div style={S.grid}>
        {today.entries != null && <Stat label="Entradas hoje" value={today.entries} />}
        {today.bookings != null && <Stat label="Reservas hoje" value={today.bookings} />}
        {today.noShows != null && <Stat label="No-shows hoje" value={today.noShows} tone={today.noShows > 0 ? 'warn' : undefined} />}
        {today.newMembers != null && <Stat label="Novos membros hoje" value={today.newMembers} tone={today.newMembers > 0 ? 'good' : undefined} />}
        {alerts && <Stat label="Em dívida" value={`${Number(alerts.totalOutstanding ?? 0).toFixed(0)}€`} tone={(alerts.overdueCount ?? 0) > 0 ? 'warn' : undefined} />}
      </div>

      <div style={S.cols}>
        {/* AGORA — últimas entradas (só com AccessControl) */}
        {caps.AccessControl && (
          <Panel title="AGORA · Últimas entradas" badge={entries.length}>
            {entries.length === 0 ? <div style={S.empty}>Sem entradas hoje.</div> : (
              <ul style={S.list}>
                {entries.map((e, i) => (
                  <li key={i} style={S.li}>
                    <span>Membro #{e.userId ?? '—'}</span>
                    <span style={{ color: e.result === 'AUTHORIZED' ? 'var(--success-text)' : 'var(--error-text)', fontVariantNumeric: 'tabular-nums' }}>
                      {e.result === 'AUTHORIZED' ? 'Autorizada' : 'Recusada'} · {fmtTime(e.timestamp)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div style={S.note}>Presença em tempo real requer eventos de saída (ainda não disponíveis) — não é mostrado "quem está dentro".</div>
          </Panel>
        )}

        {/* AGORA — recusas (só com AccessControl) */}
        {caps.AccessControl && denied.length > 0 && (
          <Panel title="AGORA · Recusas" badge={denied.length} tone="warn">
            <ul style={S.list}>
              {denied.map((d, i) => (
                <li key={i} style={S.li}>
                  <span>Membro #{d.userId ?? '—'}</span>
                  <span style={{ color: 'var(--error-text)' }}>{d.reason} · {fmtTime(d.timestamp)}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {/* AGORA — próximas aulas (só com Reservations) */}
        {caps.Reservations && (
          <Panel title="AGORA · Próximas aulas (2h)" badge={classes.length}>
            {classes.length === 0 ? <div style={S.empty}>Sem aulas nas próximas 2 horas.</div> : (
              <ul style={S.list}>
                {classes.map((c, i) => (
                  <li key={i} style={S.li}>
                    <span>{c.name || 'Aula'}</span>
                    <span style={{ color: 'var(--info-text)', fontVariantNumeric: 'tabular-nums' }}>{fmtTime(c.start)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}
      </div>

      {!caps.AccessControl && !caps.Reservations && (
        <div style={S.empty}>Sem controlo de acessos nem reservas ativos. A receção mostra apenas os contadores de hoje dos módulos ligados.</div>
      )}

      <ManualEntryDialog open={dialog === 'manual-entry'} onClose={() => setDialog(null)} onDone={() => load(true)} />
      <WhatsAppDialog open={dialog === 'whatsapp'} onClose={() => setDialog(null)} />
      <SmsDialog open={dialog === 'sms'} onClose={() => setDialog(null)} />
      <BookClassDialog open={dialog === 'book-class'} onClose={() => setDialog(null)} onDone={() => load(true)} />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === 'warn' ? 'var(--warning-text)' : tone === 'good' ? 'var(--success-text)' : 'var(--text-primary)';
  const border = tone === 'warn' ? 'var(--warning)' : tone === 'good' ? 'var(--success)' : 'var(--border-color)';
  return (
    <div style={{ ...S.stat, borderColor: border }}>
      <div style={{ ...S.statValue, color: c }}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

function Panel({ title, badge, tone, children }) {
  return (
    <section style={{ ...S.panel, borderColor: tone === 'warn' ? 'var(--warning)' : 'var(--border-color)' }}>
      <div style={S.panelHead}>
        <span style={S.panelTitle}>{title}</span>
        {badge != null && <span style={S.badge}>{badge}</span>}
      </div>
      {children}
    </section>
  );
}

const S = {
  wrap: { maxWidth: 1100, margin: '0 auto', padding: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  h2: { fontSize: 24, fontWeight: 800, margin: 0 },
  refresh: { color: 'var(--text-secondary)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 },
  refreshBtn: { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 },
  action: { padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'var(--accent-primary)', color: 'var(--text-inverse)', cursor: 'pointer', fontSize: 14 },
  actionDisabled: { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginTop: 18 },
  stat: { border: '1px solid var(--border-color)', borderRadius: 12, padding: 16, background: 'var(--bg-card)' },
  statValue: { fontSize: 28, fontWeight: 800 },
  statLabel: { color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 },
  cols: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 22 },
  panel: { border: '1px solid var(--border-color)', borderRadius: 14, padding: 16, background: 'var(--bg-card)' },
  panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  panelTitle: { fontSize: 13, fontWeight: 700, letterSpacing: '.03em', color: 'var(--info-text)', textTransform: 'uppercase' },
  badge: { background: 'var(--bg-tertiary)', color: 'var(--info-text)', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700 },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  li: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)', fontSize: 14 },
  empty: { color: 'var(--text-muted)', fontSize: 14, padding: 16 },
  note: { color: 'var(--text-muted)', fontSize: 12, marginTop: 10 },
};
